import { useRef, useState, useEffect, useCallback, useContext } from 'react';
import {
	Box,
	Button,
	VStack,
	Heading,
	Text,
	HStack,
	Image,
	ListItem,
	UnorderedList,
	Table,
	Thead,
	Tbody,
	Tr,
	Th,
	Td,
	useDisclosure,
	Modal,
	ModalOverlay,
	ModalContent,
	ModalBody,
	ModalFooter,
	Checkbox,
	Flex,
	Icon,
	ModalHeader,
	Divider,
	Spinner,
} from '@chakra-ui/react';
import { FaExclamationTriangle } from 'react-icons/fa';
import axios from 'axios';
import API_URL from '../../constants/environment';
import SessionContext from '../../contexts/SessionContext';
import { StepFooter } from './StepFooter';

interface OnboardingAssets {
	ccc1: string;
	ccc2: string;
	ccc3: string;
	ccc4: string;
	ccc5: string;
	ccc7: string;
	pt1: string;
	pt2: string;
	pt3: string;
}

interface DependencySetupStepProps {
	setCurrentStep: (step: 'locations' | 'account') => void;
	usesPt: boolean;
	onUsesPtChange: (value: boolean) => void;
}

const PARTBAY_BLUE = '#0071E3';

/* ─── TOC definitions ─── */
interface TocItem {
	id: string;
	label: string;
	indent?: boolean; // true for sub-steps
}

const CCC_TOC: TocItem[] = [
	{ id: 'ccc-overview', label: 'Overview' },
	{ id: 'ccc-steps', label: 'Step by Step' },
	{ id: 'ccc-step1', label: 'Step 1: Log in to CCC One and Navigate to Settings', indent: true },
	{ id: 'ccc-step2', label: 'Step 2: Go to Employees', indent: true },
	{ id: 'ccc-step3', label: 'Step 3: Add Employee', indent: true },
	{ id: 'ccc-step4', label: 'Step 4: Enter Employee Details', indent: true },
	{ id: 'ccc-step5', label: 'Step 5: Set Location Access', indent: true },
	{ id: 'ccc-step6', label: 'Step 6: Configure User Settings', indent: true },
	{ id: 'ccc-step7', label: 'Step 7: Create Username', indent: true },
	{ id: 'ccc-step8', label: 'Step 8: Save Changes', indent: true },
];

const PT_TOC: TocItem[] = [
	{ id: 'pt-overview', label: 'Overview' },
	{ id: 'pt-create', label: 'Step by Step — Creating a PartsTrader Account' },
	{ id: 'pt-step1', label: 'Step 1: Log into an Admin Account', indent: true },
	{ id: 'pt-step2', label: 'Step 2: Add User', indent: true },
	{ id: 'pt-step3', label: 'Step 3: Confirm User Addition', indent: true },
	{ id: 'pt-auth', label: 'Step by Step — Location Authorization' },
	{ id: 'pt-auth-step1', label: 'Step 1: Location Authorization', indent: true },
	{ id: 'pt-auth-step2', label: 'Step 2: Save Changes', indent: true },
];

type SetRefFn = (id: string) => (el: HTMLElement | null) => void;

export const DependencySetupStep = ({ setCurrentStep, usesPt, onUsesPtChange }: DependencySetupStepProps) => {
	const sessionContext = useContext(SessionContext);
	const [phase, setPhase] = useState<'warning' | 'guides' | 'pt-question'>('warning');
	const [disclaimerConfirmed, setDisclaimerConfirmed] = useState(false);
	const [activeTab, setActiveTab] = useState<'ccc' | 'pt'>('ccc');
	const [activeId, setActiveId] = useState('');
	const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);
	const [assets, setAssets] = useState<OnboardingAssets | null>(null);
	const [assetsError, setAssetsError] = useState(false);
	const { isOpen, onOpen, onClose } = useDisclosure();
	const scrollRef = useRef<HTMLDivElement>(null);
	const sectionRefs = useRef<Record<string, HTMLElement | null>>({});

	useEffect(() => {
		if (!sessionContext?.sessionId) return;
		let cancelled = false;
		axios.get<{ urls: OnboardingAssets }>(`${API_URL}/onboarding/assets`, { headers: { Authorization: sessionContext.sessionId } })
			.then(res => { if (!cancelled) setAssets(res.data.urls); })
			.catch(() => { if (!cancelled) setAssetsError(true); });
		return () => { cancelled = true; };
	}, [sessionContext?.sessionId]);

	const setRef = useCallback<SetRefFn>((id: string) => (el: HTMLElement | null) => {
		sectionRefs.current[id] = el;
	}, []);

	const tocItems = activeTab === 'ccc' ? CCC_TOC : PT_TOC;

	// Reset scroll & active id when switching tabs
	const prevTab = useRef(activeTab);
	useEffect(() => {
		if (prevTab.current !== activeTab) {
			prevTab.current = activeTab;
			sectionRefs.current = {};
			scrollRef.current?.scrollTo({ top: 0 });
		}
		setActiveId(tocItems[0]?.id ?? '');
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeTab]);

	// Scroll tracking
	useEffect(() => {
		const container = scrollRef.current;
		if (!container) return;

		const handleScroll = () => {
			const atBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 40;

			if (atBottom) {
				const lastVisible = [...tocItems].reverse().find((item) => {
					const el = sectionRefs.current[item.id];
					return el && el.offsetTop <= container.scrollTop + container.clientHeight;
				});
				if (lastVisible) {
					setActiveId(lastVisible.id);
					return;
				}
			}

			const scrollTop = container.scrollTop + 120;
			let current = tocItems[0]?.id ?? '';
			for (const item of tocItems) {
				const el = sectionRefs.current[item.id];
				if (el && el.offsetTop <= scrollTop) {
					current = item.id;
				}
			}
			setActiveId(current);
		};

		container.addEventListener('scroll', handleScroll, { passive: true });
		return () => container.removeEventListener('scroll', handleScroll);
	}, [tocItems]);

	const scrollTo = (id: string) => {
		const el = sectionRefs.current[id];
		if (el && scrollRef.current) {
			setActiveId(id);
			scrollRef.current.scrollTo({ top: el.offsetTop - 80, behavior: 'smooth' });
		}
	};

	const handleDisclaimerConfirm = () => {
		if (disclaimerConfirmed) {
			onClose();
			setCurrentStep('locations');
		}
	};

	/* ─── Phase 1: Warning screen ─── */
	if (phase === 'warning') {
		return (
			<Box
				position="relative"
				width="100%"
				flex={1}
				minH={0}
				display="flex"
				flexDirection="column"
				alignItems="center"
				justifyContent="center"
			>
				<Box maxW="440px" mx={4} borderRadius="xl" overflow="hidden" shadow="none">
					<Box bg={PARTBAY_BLUE} py={6} display="flex" flexDirection="column" alignItems="center">
						<Box position="relative" w="56px" h="56px" mb={3}>
							<Box
								position="absolute"
								inset={0}
								borderRadius="full"
								bg="whiteAlpha.300"
								sx={{
									animation: 'cautionPulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
									'@keyframes cautionPulse': {
										'0%, 100%': { transform: 'scale(1)', boxShadow: '0 0 0 0 rgba(255, 255, 255, 0.3)' },
										'50%': { transform: 'scale(1.12)', boxShadow: '0 0 0 12px rgba(255, 255, 255, 0)' },
									},
								}}
							/>
							<Flex position="relative" w="56px" h="56px" borderRadius="full" bg="whiteAlpha.200" align="center" justify="center">
								<Icon as={FaExclamationTriangle as React.ElementType} color="white" boxSize={6} />
							</Flex>
						</Box>
						<Text fontSize="lg" fontWeight="bold" color="white">Account Setup Guides</Text>
					</Box>
					<Box pt={6} pb={6} px={8} textAlign="center">
						<Text fontSize="sm" color="gray.600" mb={6}>
							Please pay careful attention to account creation and follow the setup guides
							<strong> step by step exactly</strong>. Incorrect setup will cause issues with Yaxle
							automations and will delay your onboarding.
						</Text>
						<Button
							bg={PARTBAY_BLUE}
							color="white"
							_hover={{ bg: '#3141a5' }}
							size="md"
							width="100%"
							borderRadius="md"
							onClick={() => setPhase('guides')}
						>
							I Understand
						</Button>
					</Box>
				</Box>
			</Box>
		);
	}

	/* ─── Phase 1b: PartsTrader question gate ─── */
	if (phase === 'pt-question') {
		return (
			<Box
				position="relative"
				width="100%"
				flex={1}
				minH={0}
				display="flex"
				flexDirection="column"
				alignItems="center"
				justifyContent="center"
			>
				<Box maxW="440px" mx={4} borderRadius="xl" overflow="hidden" shadow="none">
					<Box bg={PARTBAY_BLUE} py={6} display="flex" flexDirection="column" alignItems="center">
						<Text fontSize="lg" fontWeight="bold" color="white">Do you use PartsTrader?</Text>
					</Box>
					<Box pt={6} pb={6} px={8}>
						<Text fontSize="sm" color="gray.600" mb={6} textAlign="center">
							PartsTrader is optional. If your organization doesn't use PartsTrader,
							you can skip setting it up and continue with CCC ONE and OPS only.
						</Text>
						<VStack spacing={3}>
							<Button
								bg={PARTBAY_BLUE}
								color="white"
								_hover={{ bg: '#3141a5' }}
								size="md"
								width="100%"
								borderRadius="md"
								onClick={() => {
									onUsesPtChange(true);
									setActiveTab('pt');
									setPhase('guides');
								}}
							>
								Yes, show PartsTrader setup guide
							</Button>
							<Button
								variant="outline"
								size="md"
								width="100%"
								borderRadius="md"
								onClick={() => {
									onUsesPtChange(false);
									setCurrentStep('locations');
								}}
							>
								No, skip PartsTrader
							</Button>
							<Button
								variant="ghost"
								size="sm"
								onClick={() => {
									setActiveTab('ccc');
									setPhase('guides');
								}}
							>
								Back to CCC guide
							</Button>
						</VStack>
					</Box>
				</Box>
			</Box>
		);
	}

	/* ─── Phase 2: Tabbed article guides ─── */
	if (!assets && !assetsError) {
		return (
			<Box position="relative" width="100%" flex={1} minH={0} display="flex" alignItems="center" justifyContent="center">
				<VStack spacing={3}>
					<Spinner size="lg" color={PARTBAY_BLUE} />
					<Text fontSize="sm" color="gray.500">Loading setup guides…</Text>
				</VStack>
			</Box>
		);
	}

	if (assetsError || !assets) {
		return (
			<Box position="relative" width="100%" flex={1} minH={0} display="flex" alignItems="center" justifyContent="center">
				<VStack spacing={3}>
					<Text fontSize="sm" color="red.500">Failed to load guide images. Please refresh and try again.</Text>
				</VStack>
			</Box>
		);
	}

	return (
		<Box position="relative" width="100%" flex={1} minH={0} display="flex" flexDirection="column">
				{/* Content + TOC row — scrollbar is on the far right */}
			<Box flex={1} minH={0} position="relative">
				<Box
					ref={scrollRef}
					position="absolute"
					inset={0}
					overflowY="auto"
					sx={{ scrollBehavior: 'smooth' }}
				>
					<Box
						display="flex"
						px={{ base: 2, md: 8 }}
						py={6}
					>
						{/* Article content */}
						<Box flex={1} minW={0}>
							<Box maxW="640px" mx="auto">
								{activeTab === 'ccc' ? (
									<CccContent onLightbox={setLightboxSrc} setRef={setRef} assets={assets} />
								) : (
									<PtContent onLightbox={setLightboxSrc} setRef={setRef} assets={assets} />
								)}
								<Box h="20vh" />
							</Box>
						</Box>

						{/* Right-side TOC — scrolls with page, sticks in viewport */}
						<Box
							width="220px"
							flexShrink={0}
							pl={4}
							pr={2}
							display={{ base: 'none', lg: 'block' }}
						>
							<Box position="sticky" top="24px">
								<VStack align="stretch" spacing={0} borderLeftWidth="2px" borderLeftColor="gray.200" pl={0}>
									{tocItems.map((item) => {
										const isActive = item.id === activeId;
										return (
											<Box
												key={item.id}
												as="button"
												onClick={() => scrollTo(item.id)}
												textAlign="left"
												py={1.5}
												pl={item.indent ? 6 : 3}
												pr={2}
												position="relative"
												_before={isActive ? {
													content: '""',
													position: 'absolute',
													left: '-2px',
													top: 0,
													bottom: 0,
													width: '2px',
													bg: PARTBAY_BLUE,
												} : undefined}
												cursor="pointer"
												transition="all 0.15s"
											>
												<Text
													fontSize="xs"
													fontWeight={isActive ? '700' : '400'}
													color={isActive ? 'gray.900' : 'gray.500'}
													_hover={{ color: isActive ? 'gray.900' : 'gray.700' }}
													lineHeight="short"
												>
													{item.label}
												</Text>
											</Box>
										);
									})}
								</VStack>
							</Box>
						</Box>
					</Box>
				</Box>
			</Box>

			<StepFooter
				back={{
					onClick: () => setPhase(activeTab === 'ccc' ? 'warning' : 'pt-question'),
				}}
				primary={{
					label: activeTab === 'ccc' ? 'Next' : 'Continue',
					onClick: activeTab === 'ccc' ? () => setPhase('pt-question') : onOpen,
				}}
			/>

			{/* Lightbox viewer */}
			<Modal isOpen={!!lightboxSrc} onClose={() => setLightboxSrc(null)} size="6xl" isCentered>
				<ModalOverlay bg="blackAlpha.800" />
				<ModalContent bg="transparent" shadow="none" maxW="90vw">
					<ModalBody p={0} display="flex" justifyContent="center" alignItems="center">
						{lightboxSrc && (
							<Image
								src={lightboxSrc}
								maxH="85vh"
								maxW="100%"
								objectFit="contain"
								borderRadius="lg"
								cursor="zoom-out"
								onClick={() => setLightboxSrc(null)}
							/>
						)}
					</ModalBody>
				</ModalContent>
			</Modal>

			{/* Disclaimer modal */}
			<Modal isOpen={isOpen} onClose={() => { setDisclaimerConfirmed(false); onClose(); }} isCentered size="md">
				<ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
				<ModalContent mx={4} borderRadius="xl" overflow="hidden" shadow="none">
					<Box bg={PARTBAY_BLUE} py={6} display="flex" flexDirection="column" alignItems="center">
						<Box position="relative" w="56px" h="56px" mb={3}>
							<Box
								position="absolute"
								inset={0}
								borderRadius="full"
								bg="whiteAlpha.300"
								sx={{
									animation: 'cautionPulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
									'@keyframes cautionPulse': {
										'0%, 100%': { transform: 'scale(1)', boxShadow: '0 0 0 0 rgba(255, 255, 255, 0.3)' },
										'50%': { transform: 'scale(1.12)', boxShadow: '0 0 0 12px rgba(255, 255, 255, 0)' },
									},
								}}
							/>
							<Flex position="relative" w="56px" h="56px" borderRadius="full" bg="whiteAlpha.200" align="center" justify="center">
								<Icon as={FaExclamationTriangle as React.ElementType} color="white" boxSize={6} />
							</Flex>
						</Box>
						<ModalHeader p={0} textAlign="center">
							<Text fontSize="lg" fontWeight="bold" color="white">Before You Continue</Text>
						</ModalHeader>
					</Box>
					<ModalBody pt={6} pb={4} px={8}>
						<Text fontSize="sm" color="gray.600" textAlign="center" mb={5}>
							Please confirm that you have completed the account setup steps outlined above. Skipping this will significantly impact your onboarding experience.
						</Text>
						<Flex justify="center">
							<Checkbox colorScheme="blue" isChecked={disclaimerConfirmed} onChange={(e) => setDisclaimerConfirmed(e.target.checked)} size="md">
								<Text as="span" fontSize="sm" color="gray.700">I have completed the setup steps</Text>
							</Checkbox>
						</Flex>
					</ModalBody>
					<ModalFooter pb={6} px={8} justifyContent="center" gap={3}>
						<Button size="action" variant="outline" onClick={() => { setDisclaimerConfirmed(false); onClose(); }} borderRadius="md" px={6}>
							Go Back
						</Button>
					<Button bg={PARTBAY_BLUE} color="white" _hover={{ bg: '#3141a5' }} onClick={handleDisclaimerConfirm} size="action" borderRadius="md" px={6} isDisabled={!disclaimerConfirmed}>
						Continue
					</Button>
					</ModalFooter>
				</ModalContent>
			</Modal>
		</Box>
	);
};

/* ─── CCC ONE Content ─── */
const CccContent = ({ onLightbox, setRef, assets }: { onLightbox: (src: string) => void; setRef: SetRefFn; assets: OnboardingAssets }) => (
	<>
		<Box mb={6}>
			<Heading size="lg" mb={2} color="gray.800">Creating a CCC ONE Account</Heading>
		</Box>

		{/* Overview */}
		<Box ref={setRef('ccc-overview')} mb={6}>
			<Heading size="md" mb={3} color="gray.700">Overview</Heading>
			<Divider mb={4} />
			<Text fontSize="sm" color="gray.600" lineHeight="tall" textAlign="left">
				Setting up Yaxle is the same as onboarding a new employee to the company. This means that you'll need to create accounts for us to use in your daily software. This guide will walk you through how to setup CCC ONE on our behalf.
			</Text>
		</Box>

		{/* Cautionary Notes */}
		<Box bg="yellow.50" p={5} borderRadius="lg" borderWidth={1} borderColor="yellow.200" mb={10} mx="auto" maxW="100%">
			<Text fontSize="sm" fontWeight="bold" color="yellow.800" mb={2}>Cautionary Notes</Text>
			<UnorderedList fontSize="sm" color="yellow.700" pl={4} spacing={2}>
				<ListItem>Ensure that you enter your own email address correctly to receive any verification codes and test sign in before giving us the credentials</ListItem>
				<ListItem>Do not adjust any settings without prior approval if you are not an Admin</ListItem>
			</UnorderedList>
		</Box>

		{/* Step by Step */}
		<Box ref={setRef('ccc-steps')} mb={6}>
			<Heading size="md" mb={3} color="gray.700">Step by Step</Heading>
			<Divider mb={4} />
		</Box>

		<VStack spacing={12} align="stretch">
			<Box ref={setRef('ccc-step1')}>
				<StepHeading number="1" title="Log in to CCC One and Navigate to Settings" />
				<Image src={assets.ccc1} borderRadius="lg" maxW="100%" mx="auto" display="block" mb={4} shadow="none" cursor="zoom-in" onClick={() => onLightbox(assets.ccc1)} _hover={{ shadow: 'none' }} transition="box-shadow 0.2s" />
				<Text fontSize="sm" color="gray.600" textAlign="left">
					Once logged in, locate and click on the <strong>'Settings'</strong> option in the bottom left hand corner.
				</Text>
			</Box>

			<Box ref={setRef('ccc-step2')}>
				<StepHeading number="2" title="Go to Employees" />
				<Text fontSize="sm" color="gray.600" mb={4} textAlign="left">
					In the Settings menu, select <strong>'Employees'</strong>.
				</Text>
				<Image src={assets.ccc2} borderRadius="lg" maxW="100%" mx="auto" display="block" shadow="none" cursor="zoom-in" onClick={() => onLightbox(assets.ccc2)} _hover={{ shadow: 'none' }} transition="box-shadow 0.2s" />
			</Box>

			<Box ref={setRef('ccc-step3')}>
				<StepHeading number="3" title="Add Employee" />
				<Text fontSize="sm" color="gray.600" mb={4} textAlign="left">
					Click on <strong>'Add'</strong> at the top left.
				</Text>
				<Image src={assets.ccc3} borderRadius="lg" maxW="100%" mx="auto" display="block" shadow="none" cursor="zoom-in" onClick={() => onLightbox(assets.ccc3)} _hover={{ shadow: 'none' }} transition="box-shadow 0.2s" />
			</Box>

			<Box ref={setRef('ccc-step4')}>
				<StepHeading number="4" title="Enter Employee Details" />
				<Text fontSize="sm" color="gray.600" mb={4} textAlign="left">
					Fill in the following fields:
				</Text>
				<Box mx="auto" maxW="480px">
					<Table size="sm" variant="simple" mb={4}>
						<Thead>
							<Tr><Th>Fields</Th><Th>Information</Th></Tr>
						</Thead>
						<Tbody>
							<Tr><Td>First Name</Td><Td>Central</Td></Tr>
							<Tr><Td>Last Name</Td><Td>Parts</Td></Tr>
							<Tr><Td>Email</Td><Td fontSize="xs">Enter your own email address so you can finish setting up the account and test the login</Td></Tr>
						</Tbody>
					</Table>
				</Box>
				<Image src={assets.ccc4} borderRadius="lg" maxW="100%" mx="auto" display="block" shadow="none" cursor="zoom-in" onClick={() => onLightbox(assets.ccc4)} _hover={{ shadow: 'none' }} transition="box-shadow 0.2s" />
			</Box>

			<Box ref={setRef('ccc-step5')}>
				<StepHeading number="5" title="Set Location Access" />
				<UnorderedList fontSize="sm" color="gray.600" pl={4} mb={4} spacing={1} textAlign="left" mx="auto" maxW="480px">
					<ListItem>Go to <strong>'Locations'</strong></ListItem>
					<ListItem>Select <strong>'All Locations'</strong> to ensure access to every location</ListItem>
				</UnorderedList>
				<Image src={assets.ccc5} borderRadius="lg" maxW="100%" mx="auto" display="block" shadow="none" cursor="zoom-in" onClick={() => onLightbox(assets.ccc5)} _hover={{ shadow: 'none' }} transition="box-shadow 0.2s" />
			</Box>

			<Box ref={setRef('ccc-step6')}>
				<StepHeading number="6" title="Configure User Settings" />
				<UnorderedList fontSize="sm" color="gray.600" pl={4} spacing={1} textAlign="left" mx="auto" maxW="480px">
					<ListItem>Enable the user account</ListItem>
					<ListItem>Set the user type to <strong>'Standard User'</strong></ListItem>
					<ListItem>Make sure to grant Admin privileges to the team for easier management. We will not make any changes without your discretion but need it to quickly debug any errors that come about with the AI automations</ListItem>
				</UnorderedList>
			</Box>

			<Box ref={setRef('ccc-step7')}>
				<StepHeading number="7" title="Create Username" />
				<Image src={assets.ccc7} borderRadius="lg" maxW="100%" mx="auto" display="block" mb={4} shadow="none" cursor="zoom-in" onClick={() => onLightbox(assets.ccc7)} _hover={{ shadow: 'none' }} transition="box-shadow 0.2s" />
				<UnorderedList fontSize="sm" color="gray.600" pl={4} spacing={1} textAlign="left" mx="auto" maxW="480px">
					<ListItem>Enter a username, typically <strong>'Central Parts'</strong>.</ListItem>
					<ListItem>If that username is taken, use <strong>'Shop Central Parts'</strong>.</ListItem>
				</UnorderedList>
			</Box>

			<Box ref={setRef('ccc-step8')}>
				<StepHeading number="8" title="Save Changes" />
				<Text fontSize="sm" color="gray.600" textAlign="left">
					After entering all necessary information, click <strong>'Save'</strong> to finalize the setup.
				</Text>
			</Box>
		</VStack>
	</>
);

/* ─── PartsTrader Content ─── */
const PtContent = ({ onLightbox, setRef, assets }: { onLightbox: (src: string) => void; setRef: SetRefFn; assets: OnboardingAssets }) => (
	<>
		<Box mb={6}>
			<Heading size="lg" mb={2} color="gray.800">Creating a PartsTrader Account</Heading>
		</Box>

		{/* Overview */}
		<Box ref={setRef('pt-overview')} mb={10}>
			<Heading size="md" mb={3} color="gray.700">Overview</Heading>
			<Divider mb={4} />
			<Text fontSize="sm" color="gray.600" lineHeight="tall" textAlign="left">
				Setting up Yaxle is the same as onboarding a new employee to the company. This means that you'll need to create accounts for us to use in your daily software. This guide will walk you through how to setup PartsTrader on our behalf.
			</Text>
		</Box>

		{/* Step by Step — Creating a PartsTrader Account */}
		<Box ref={setRef('pt-create')} mb={6}>
			<Heading size="md" mb={3} color="gray.700">Step by Step — Creating a PartsTrader Account</Heading>
			<Divider mb={4} />
		</Box>

		<VStack spacing={12} align="stretch" mb={12}>
			<Box ref={setRef('pt-step1')}>
				<StepHeading number="1" title="Log into an Admin Account" />
				<Text fontSize="sm" color="gray.600" mb={4} textAlign="left">
					Ensure you are logged into an admin account on PartsTrader and then navigate to the Profile section at the top left next to the PartsTrader logo.
				</Text>
				<Image src={assets.pt1} borderRadius="lg" maxW="100%" mx="auto" display="block" shadow="none" cursor="zoom-in" onClick={() => onLightbox(assets.pt1)} _hover={{ shadow: 'none' }} transition="box-shadow 0.2s" />
			</Box>

			<Box ref={setRef('pt-step2')}>
				<StepHeading number="2" title="Add User" />
				<Text fontSize="sm" color="gray.600" mb={2} textAlign="left">
					Under <strong>'My Company'</strong>, select <strong>'Add User'</strong>.
				</Text>
				<Text fontSize="sm" color="gray.600" mb={4} textAlign="left">
					Fill in the following details:
				</Text>
				<Box mx="auto" maxW="480px">
					<Table size="sm" variant="simple" mb={4}>
						<Thead>
							<Tr><Th>Fields</Th><Th>Information</Th></Tr>
						</Thead>
						<Tbody>
							<Tr><Td>First Name</Td><Td>Central</Td></Tr>
							<Tr><Td>Last Name</Td><Td>Parts</Td></Tr>
							<Tr><Td>Username</Td><Td fontSize="xs">[shop name]-central-parts (e.g., west-collision-central-parts)</Td></Tr>
							<Tr><Td>Job Title</Td><Td fontSize="xs">Central Parts Procurement Manager</Td></Tr>
							<Tr><Td>Email Address</Td><Td fontSize="xs">Enter your own email address so you can finish setting up the account and test the login</Td></Tr>
							<Tr><Td>Primary Responsibility</Td><Td>Administrator</Td></Tr>
							<Tr><Td>Roles</Td><Td fontSize="xs">All except for repairer auditor</Td></Tr>
						</Tbody>
					</Table>
				</Box>
				<Image src={assets.pt2} borderRadius="lg" maxW="100%" mx="auto" display="block" shadow="none" cursor="zoom-in" onClick={() => onLightbox(assets.pt2)} _hover={{ shadow: 'none' }} transition="box-shadow 0.2s" />
			</Box>

			<Box ref={setRef('pt-step3')}>
				<StepHeading number="3" title="Confirm User Addition" />
				<UnorderedList fontSize="sm" color="gray.600" pl={4} spacing={1} textAlign="left" mx="auto" maxW="480px">
					<ListItem>Click <strong>'Add User'</strong></ListItem>
					<ListItem>Check your email for a confirmation link to complete the account creation</ListItem>
				</UnorderedList>
			</Box>
		</VStack>

		{/* Step by Step — Location Authorization */}
		<Box ref={setRef('pt-auth')} mb={6}>
			<Heading size="md" mb={3} color="gray.700">Step by Step — Location Authorization</Heading>
			<Divider mb={4} />
		</Box>

		<VStack spacing={12} align="stretch">
			<Box ref={setRef('pt-auth-step1')}>
				<StepHeading number="1" title="Location Authorization" />
				<UnorderedList fontSize="sm" color="gray.600" pl={4} mb={4} spacing={1} textAlign="left" mx="auto" maxW="480px">
					<ListItem>After confirming the account, go to <strong>Profile &gt; My Company &gt; Location Authorization</strong></ListItem>
					<ListItem>Search for your username</ListItem>
					<ListItem>Check off <strong>All locations</strong> — we need access to all locations even if we're starting with a few</ListItem>
				</UnorderedList>
				<Image src={assets.pt3} borderRadius="lg" maxW="100%" mx="auto" display="block" shadow="none" cursor="zoom-in" onClick={() => onLightbox(assets.pt3)} _hover={{ shadow: 'none' }} transition="box-shadow 0.2s" />
			</Box>

			<Box ref={setRef('pt-auth-step2')}>
				<StepHeading number="2" title="Save Changes" />
				<Text fontSize="sm" color="gray.600" textAlign="left">
					Click <strong>'Save & Apply'</strong> to finalize the location authorization.
				</Text>
			</Box>
		</VStack>
	</>
);

/* ─── Step heading helper ─── */
const StepHeading = ({ number, title }: { number: string; title: string }) => (
	<HStack spacing={3} mb={4} justify="flex-start" align="center">
		<Flex
			w="28px"
			h="28px"
			borderRadius="full"
			bg={PARTBAY_BLUE}
			color="white"
			align="center"
			justify="center"
			fontSize="xs"
			fontWeight="bold"
			flexShrink={0}
		>
			{number}
		</Flex>
		<Heading size="sm" color="gray.800" textAlign="left">{title}</Heading>
	</HStack>
);
